"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { Title } from "@/components/common/typography/Title";
import { faqData } from "@/data/faq";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";

export const FAQSection = () => {
  const t = useTranslations("faq");
  const [openAccordions, setOpenAccordions] = useState<Set<number>>(new Set());

  // Mapper les FAQ avec les traductions
  const translatedFaqData = faqData.map((item) => ({
    question: t(item.questionKey),
    answer: t(item.answerKey),
  }));

  const toggleAccordion = (index: number) => {
    const newOpenAccordions = new Set(openAccordions);
    if (newOpenAccordions.has(index)) {
      newOpenAccordions.delete(index);
    } else {
      newOpenAccordions.add(index);
    }
    setOpenAccordions(newOpenAccordions);
  };

  // Variants pour les animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <SectionLayout>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-50px" }}
        className="w-full"
      >
        <Title title={t("section.title")} subtitle={t("section.subtitle")} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
        className="w-full rounded-xl bg-tertiary p-2"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {translatedFaqData.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="mb-2 last:mb-0"
            >
              <div
                className="cursor-pointer rounded-md bg-primary p-4"
                onClick={() => toggleAccordion(index)}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-medium text-secondary">
                    {item.question}
                  </h3>
                  <motion.div
                    animate={{
                      rotate: openAccordions.has(index) ? 180 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    style={{ transformOrigin: "center" }}
                    className="flex w-8 flex-shrink-0 justify-center"
                  >
                    <Icon
                      icon="mdi:chevron-down"
                      className={`text-xl transition-colors duration-300 ${
                        openAccordions.has(index)
                          ? "text-secondary"
                          : "text-accent"
                      }`}
                    />
                  </motion.div>
                </div>
                <AnimatePresence>
                  {openAccordions.has(index) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 pt-2">
                        <p className="leading-relaxed text-accent">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </SectionLayout>
  );
};
